import { Button, Card, Flex, Typography } from 'antd'
import { ArrowRight, Settings } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { DateTimeCell, WorkspaceDemoBadge } from 'src/components'
import { useHasWorkspacePermissions } from 'src/hooks'
import { paths } from 'src/router'
import {
  getInitials,
  greetingKey,
  stringToHslColor,
  WorkspacePermissionCode
} from 'src/utils'

import { useStyles } from './styles'

const { Text, Title } = Typography

type Props = {
  workspaceId: string
  workspaceName: string | undefined
  workspaceDomain: string | undefined | null
  createdAt: string | undefined
  firstName: string | undefined
  accessRole: string | undefined
  isDemo: boolean
  demoExpiresAt: string | null
  /** The workspace's own picture, when one was uploaded. */
  photoUrl?: string
}

export const OverviewHero = ({
  workspaceId,
  workspaceName,
  workspaceDomain,
  createdAt,
  firstName,
  accessRole,
  isDemo,
  demoExpiresAt,
  photoUrl
}: Props) => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const mayManageWorkspace = useHasWorkspacePermissions(
    WorkspacePermissionCode.ORGANIZATION_MANAGE
  )

  const greeting = useMemo(
    () => t(`workspaceOverview.greeting.${greetingKey()}`),
    [t]
  )

  const initials = useMemo(
    () => getInitials(workspaceName ?? '') || '?',
    [workspaceName]
  )

  // Same name-derived colour the sidebar tile and the onboarding preview use,
  // so the workspace wears one mark everywhere. The class keeps a brand
  // fallback for the frame where the name is not known yet.
  const tileBackground = photoUrl
    ? undefined
    : workspaceName
      ? stringToHslColor(workspaceName)
      : undefined
  const workspaceHref = paths.workspaces.home(workspaceId)
  const goSettings = () => navigate(paths.workspaces.settings(workspaceId))
  const goWorkspace = () => navigate(workspaceHref)

  return (
    <Card variant="outlined" className={styles.hero}>
      <Flex align="center" justify="space-between" gap={16} wrap="wrap">
        <Link
          to={workspaceHref}
          className={styles.heroIdentityLink}
          aria-label={t('workspaceOverview.hero.openWorkspace')}
        >
          <div
            className={cx(
              styles.heroAvatar,
              photoUrl && styles.heroAvatarWithImage
            )}
            style={{ background: tileBackground }}
            aria-hidden
          >
            {photoUrl ? (
              <img src={photoUrl} alt="" className={styles.heroAvatarImage} />
            ) : (
              initials
            )}
          </div>
          <Flex vertical gap={2} style={{ minWidth: 0 }}>
            <Text className={styles.heroGreeting}>
              {greeting}
              {firstName
                ? `, ${firstName}.`
                : `, ${t('workspaceOverview.greeting.fallbackName')}.`}
            </Text>
            <Title level={3} className={styles.heroTitle} ellipsis>
              {workspaceName ?? t('workspaceOverview.fallbackWorkspace')}
            </Title>
          </Flex>
        </Link>

        <Flex align="center" gap={8} wrap>
          {mayManageWorkspace ? (
            <Button
              type="text"
              icon={<Settings size={14} />}
              onClick={goSettings}
            >
              {t('workspaceOverview.hero.manage')}
            </Button>
          ) : null}
          <Button
            type="primary"
            icon={<ArrowRight size={14} />}
            iconPlacement="end"
            onClick={goWorkspace}
          >
            {t('workspaceOverview.hero.openWorkspace')}
          </Button>
        </Flex>
      </Flex>

      <div className={styles.heroDivider} />

      <Flex gap={28} wrap="wrap">
        <Flex vertical gap={2}>
          <Text className={styles.heroMetaLabel}>
            {t('workspaceOverview.hero.domain')}
          </Text>
          {workspaceDomain ? (
            <Text className={styles.heroMetaValue} copyable>
              {workspaceDomain}
            </Text>
          ) : (
            <Text className={styles.heroMetaValue} type="secondary">
              {t('workspaceOverview.hero.noDomain')}
            </Text>
          )}
        </Flex>

        <Flex vertical gap={2}>
          <Text className={styles.heroMetaLabel}>
            {t('workspaceOverview.hero.created')}
          </Text>
          <DateTimeCell
            value={createdAt}
            variant="date"
            style={{ fontSize: 12, fontWeight: 500 }}
          />
        </Flex>

        <Flex vertical gap={2}>
          <Text className={styles.heroMetaLabel}>
            {t('workspaceOverview.hero.role')}
          </Text>
          <Text className={styles.heroMetaValue}>
            {accessRole ?? t('ui.noValue')}
          </Text>
        </Flex>

        <Flex vertical gap={2}>
          <Text className={styles.heroMetaLabel}>
            {t('workspace.overviewPage.workspaceId')}
          </Text>
          <Text className={styles.heroMetaValue} copyable>
            {workspaceId}
          </Text>
        </Flex>

        {isDemo ? (
          <Flex vertical gap={2}>
            <Text className={styles.heroMetaLabel}>
              {t('workspaceOverview.demo.label')}
            </Text>
            <WorkspaceDemoBadge
              isDemo={isDemo}
              expiresAt={demoExpiresAt}
              showRemainingDays
              showLabel={false}
            />
          </Flex>
        ) : null}
      </Flex>
    </Card>
  )
}
